import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Search, Plus, Upload, X, Loader2, User as UserIcon, FileText, Briefcase, MessageCircle, ClipboardCheck, Activity, ShieldCheck, AlertTriangle, Mail, Phone, MapPin, CalendarDays, Calendar, TrendingUp, Send } from "lucide-react";
import { toast } from "sonner";

const QUOTAS = ["A", "B", "C", "D"];

export default function AdminSoci() {
    const [soci, setSoci] = useState([]);
    const [search, setSearch] = useState("");
    const [quotaFilter, setQuotaFilter] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = useCallback(() => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (quotaFilter) params.set("quota", quotaFilter);
        apiPrivate.get(`/admin/soci?${params}`).then(r => setSoci(r.data)).catch(() => {});
    }, [search, quotaFilter]);
    useEffect(() => { load(); }, [load]);

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-soci">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Gestione soci</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">Libro <span className="font-serif-italic text-acid">soci</span>.</h1>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setShowImport(true)} className="btn-ghost" data-testid="btn-import-excel"><Upload className="h-4 w-4" /> Import Excel</button>
                        <button onClick={() => setShowCreate(true)} className="btn-rebel" data-testid="btn-new-socio"><Plus className="h-4 w-4" /> Nuovo socio</button>
                    </div>
                </div>

                <div className="mt-8 flex gap-3 flex-wrap items-center">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cerca per nome, email, CF…"
                            data-testid="search-soci"
                            className="w-full bg-[#0F0F0F] border border-zinc-800 text-white pl-12 pr-4 py-3 font-grotesk focus:outline-none focus:border-acid"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setQuotaFilter("")} data-testid="qf-all" className={`px-3 py-2 text-xs uppercase tracking-widest font-grotesk border ${quotaFilter === "" ? "border-acid text-acid" : "border-zinc-800 text-zinc-500"}`}>Tutte</button>
                        {QUOTAS.map(q => (
                            <button key={q} onClick={() => setQuotaFilter(q)} data-testid={`qf-${q}`} className={`px-3 py-2 text-xs uppercase tracking-widest font-grotesk border ${quotaFilter === q ? "border-acid text-acid" : "border-zinc-800 text-zinc-500"}`}>{q}</button>
                        ))}
                    </div>
                </div>

                <div className="bento mt-6 p-0 overflow-x-auto" data-testid="soci-table-wrapper">
                    <table className="w-full text-sm" data-testid="soci-table">
                        <thead className="border-b border-zinc-900 text-left">
                            <tr className="font-grotesk uppercase text-xs tracking-widest text-zinc-500">
                                <th className="p-4">Nome / Ragione sociale</th>
                                <th className="p-4">Email</th>
                                <th className="p-4">Quota</th>
                                <th className="p-4 text-right">Valore</th>
                                <th className="p-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {soci.length === 0 && (
                                <tr><td colSpan={5} className="p-10 text-center text-zinc-500" data-testid="soci-empty">Nessun socio trovato</td></tr>
                            )}
                            {soci.map(s => (
                                <tr key={s.id} className="border-b border-zinc-900 hover:bg-[#0a0a0a] transition" data-testid={`socio-row-${s.id}`}>
                                    <td className="p-4 font-grotesk">{s.ragione_sociale || `${s.nome || ""} ${s.cognome || ""}`.trim() || "—"}</td>
                                    <td className="p-4 text-zinc-400">{s.email}</td>
                                    <td className="p-4">
                                        <div className="flex gap-1">{(s.quota_categories || []).map(q => <span key={q} className="bg-acid text-black text-[10px] font-bold px-2 py-0.5 font-grotesk">{q}</span>)}</div>
                                    </td>
                                    <td className="p-4 text-right font-grotesk">{s.valore_nominale_quota ? `€${Number(s.valore_nominale_quota).toLocaleString("it-IT")}` : "—"}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => setDetail(s)} className="text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-acid" data-testid={`view-${s.id}`}>Apri</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {detail && <SocioDetail socio={detail} onClose={() => setDetail(null)} onReload={load} />}
                {showCreate && <SocioCreate onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
                {showImport && <SocioImport onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
            </div>
        </AppLayout>
    );
}

function Modal({ children, onClose, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
}

function SocioDetail({ socio, onClose, onReload }) {
    const [tab, setTab] = useState("anagrafica");
    const [full, setFull] = useState(null);
    const [form, setForm] = useState({ ...socio });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const r = await apiPrivate.get(`/admin/soci/${socio.id}/full`);
        setFull(r.data);
        setForm({
            ...r.data.profile,
            data_nascita: r.data.extras.data_nascita || "",
            ragione_sociale: r.data.extras.ragione_sociale || "",
            partita_iva: r.data.extras.partita_iva || "",
        });
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [socio.id]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            const initial = full.profile;
            for (const k of ["email", "nome", "cognome", "ragione_sociale", "codice_fiscale", "partita_iva", "pec", "telefono", "indirizzo", "data_nascita", "is_active"]) {
                if ((form[k] || null) !== (initial[k] || null)) payload[k] = form[k];
            }
            if (Object.keys(payload).length === 0) {
                toast.info("Nessuna modifica da salvare");
                setSaving(false);
                return;
            }
            await apiPrivate.patch(`/admin/soci/${socio.id}`, payload);
            toast.success("Modifiche salvate");
            onReload();
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setSaving(false); }
    };

    return (
        <Modal onClose={onClose} testid="socio-detail-modal">
            <div className="p-8">
                {/* Header */}
                <div className="flex justify-between items-start gap-4 mb-2">
                    <div className="min-w-0">
                        <span className="label-tag text-magenta">Scheda socio</span>
                        <h2 className="font-display text-3xl tracking-tighter mt-2 truncate">
                            {form.ragione_sociale || `${form.nome || ""} ${form.cognome || ""}`.trim() || form.email}
                        </h2>
                        <p className="text-zinc-400 text-sm mt-1">{form.email}</p>
                    </div>
                    <button onClick={onClose} data-testid="detail-close"><X className="h-5 w-5 text-zinc-500" /></button>
                </div>

                {loading || !full ? (
                    <div className="p-10 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : (
                    <>
                        {/* Header KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5" data-testid="socio-header-kpi">
                            <MiniStat label="Quote" value={(form.quota_categories || []).map(q => `Q${q}`).join(" ") || "—"} tone="bayside" />
                            <MiniStat label="Profilo" value={`${full.completion.percent}%`} tone={full.completion.percent === 100 ? "emerald" : full.completion.percent > 50 ? "magenta" : "vermillion"} />
                            <MiniStat label={form.is_active ? "Attivo" : "Disattivo"} value={form.is_active ? "ON" : "OFF"} tone={form.is_active ? "emerald" : "vermillion"} />
                            <MiniStat label="Da" value={full.profile.created_at ? new Date(full.profile.created_at).toLocaleDateString("it-IT") : "—"} tone="zinc" />
                        </div>

                        {/* Tabs */}
                        <div className="mt-6 flex items-center gap-1 border-b border-white/10 overflow-x-auto" data-testid="socio-tabs">
                            <TabBtn active={tab === "anagrafica"} onClick={() => setTab("anagrafica")} testid="tab-anagrafica" icon={UserIcon}>Anagrafica</TabBtn>
                            <TabBtn active={tab === "quote"} onClick={() => setTab("quote")} testid="tab-quote" icon={TrendingUp}>Quote & Investimento</TabBtn>
                            <TabBtn active={tab === "attivita"} onClick={() => setTab("attivita")} testid="tab-attivita" icon={Activity}>Attività</TabBtn>
                            <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} testid="tab-audit" icon={ShieldCheck}>Audit ({full.audit_history.length})</TabBtn>
                        </div>

                        {/* Tab content */}
                        {tab === "anagrafica" && (
                            <div className="mt-6 space-y-5" data-testid="tab-content-anagrafica">
                                {/* Completion bar if not 100% */}
                                {full.completion.percent < 100 && (
                                    <div className="bento p-4 border-l-4 border-l-magenta" data-testid="completion-banner">
                                        <div className="flex items-center justify-between text-xs font-grotesk">
                                            <span className="text-magenta uppercase tracking-widest">Profilo incompleto</span>
                                            <span className="text-zinc-400">{full.completion.percent}%</span>
                                        </div>
                                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                                            <div className="h-full bg-[linear-gradient(90deg,#ed4735,#df46e7,#653fcd)]" style={{ width: `${full.completion.percent}%` }} />
                                        </div>
                                        <p className="text-zinc-400 text-xs mt-2">
                                            Campi mancanti: {[
                                                ...full.completion.missing_contact_fields,
                                                ...full.completion.missing_identity_fields,
                                            ].join(", ") || "nessuno"}
                                        </p>
                                    </div>
                                )}
                                {/* Persona fisica/giuridica section */}
                                <SectionTitle>Dati identificativi</SectionTitle>
                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Nome" v={form.nome} on={(v) => setForm(s => ({ ...s, nome: v }))} testid="d-nome" />
                                    <Input label="Cognome" v={form.cognome} on={(v) => setForm(s => ({ ...s, cognome: v }))} testid="d-cognome" />
                                    <Input label="Ragione sociale" v={form.ragione_sociale} on={(v) => setForm(s => ({ ...s, ragione_sociale: v }))} testid="d-rs" />
                                    <Input label="Data nascita" type="date" v={form.data_nascita ? String(form.data_nascita).slice(0, 10) : ""} on={(v) => setForm(s => ({ ...s, data_nascita: v }))} testid="d-dn" />
                                    <Input label="Codice fiscale" v={form.codice_fiscale} on={(v) => setForm(s => ({ ...s, codice_fiscale: v }))} testid="d-cf" />
                                    <Input label="Partita IVA" v={form.partita_iva} on={(v) => setForm(s => ({ ...s, partita_iva: v }))} testid="d-piva" />
                                </div>
                                <SectionTitle>Contatti</SectionTitle>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <Input label="Email (login / notifiche)" v={form.email} on={(v) => setForm(s => ({ ...s, email: v }))} testid="d-email" type="email" />
                                        <p className="text-xs text-zinc-500 mt-1">L&apos;email è usata per il login OTP + notifiche. Modificarla cambia le credenziali di accesso del socio.</p>
                                    </div>
                                    <Input label="PEC" v={form.pec} on={(v) => setForm(s => ({ ...s, pec: v }))} testid="d-pec" type="email" />
                                    <Input label="Telefono" v={form.telefono} on={(v) => setForm(s => ({ ...s, telefono: v }))} testid="d-tel" />
                                    <div className="col-span-2">
                                        <Input label="Indirizzo / Sede legale" v={form.indirizzo} on={(v) => setForm(s => ({ ...s, indirizzo: v }))} testid="d-addr" />
                                    </div>
                                </div>
                                <SectionTitle>Stato account</SectionTitle>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bento p-3 flex items-center justify-between">
                                        <div>
                                            <div className="label-tag">Attivo</div>
                                            <div className="text-xs text-zinc-500 font-grotesk mt-1">Se OFF il socio non può accedere</div>
                                        </div>
                                        <button onClick={() => setForm(s => ({ ...s, is_active: !s.is_active }))} data-testid="d-toggle-active" className={`h-7 w-12 rounded-full border transition relative ${form.is_active ? "border-emerald-400 bg-emerald-500/20" : "border-zinc-700 bg-transparent"}`}>
                                            <span className={`absolute top-0.5 h-5 w-5 rounded-full transition-all ${form.is_active ? "left-6 bg-emerald-400" : "left-0.5 bg-zinc-600"}`} />
                                        </button>
                                    </div>
                                    {full.extras.email_bounced && (
                                        <div className="bento p-3 border-l-4 border-l-vermillion flex items-start gap-2">
                                            <AlertTriangle className="h-4 w-4 text-vermillion shrink-0 mt-0.5" />
                                            <div className="text-xs">
                                                <div className="text-vermillion font-grotesk uppercase tracking-widest">Email bounced</div>
                                                <p className="text-zinc-400 mt-1">Le ultime email non sono state recapitate. Verifica l'indirizzo.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {tab === "quote" && (
                            <HoldingsTab socioId={socio.id} onChanged={() => { onReload(); load(); }} />
                        )}

                        {tab === "attivita" && (
                            <div className="mt-6 space-y-5" data-testid="tab-content-attivita">
                                <SectionTitle>Documenti</SectionTitle>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Stat label="Totali" value={full.stats.documents_total} icon={FileText} tone="bayside" />
                                    <Stat label="Individuali" value={full.stats.documents_individual} tone="bayside" />
                                    <Stat label="Da gruppo quota" value={full.stats.documents_group} tone="magenta" />
                                    <Stat label="Non letti" value={full.stats.documents_unread} tone={full.stats.documents_unread > 0 ? "vermillion" : "emerald"} />
                                </div>

                                <SectionTitle>Interazioni</SectionTitle>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Stat label="Comunicazioni ricevute" value={full.stats.comms_received} icon={Send} tone="magenta" />
                                    <Stat label="Richieste chatbot" value={full.stats.chatbot_requests} icon={MessageCircle} tone="bayside" />
                                    <Stat label="Risposte sondaggi" value={full.stats.survey_responses} icon={ClipboardCheck} tone="emerald" />
                                    <Stat label="Inserzioni mercato" value={full.stats.listings_active + full.stats.listings_archived} icon={Briefcase} tone="vermillion" />
                                </div>

                                <SectionTitle>Timestamp</SectionTitle>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <DateRow icon={Calendar} label="Iscrizione piattaforma" value={full.profile.created_at} />
                                    <DateRow icon={Calendar} label="Ultimo aggiornamento dati" value={full.profile.last_data_update} />
                                    <DateRow icon={CalendarDays} label="Onboarding completato" value={full.extras.onboarding_completed_at} />
                                    <DateRow icon={Mail} label="Ultimo promemoria 6m inviato" value={full.extras.data_reminder_sent_at || "—"} />
                                </div>
                            </div>
                        )}

                        {tab === "audit" && (
                            <div className="mt-6 space-y-3" data-testid="tab-content-audit">
                                {full.audit_history.length === 0 ? (
                                    <div className="bento p-10 text-center text-zinc-500">Nessun evento di audit registrato.</div>
                                ) : (
                                    full.audit_history.map(a => (
                                        <div key={a.id} className="bento p-3 flex items-start gap-3" data-testid={`audit-row-${a.id}`}>
                                            <Activity className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between gap-2">
                                                    <span className="text-xs font-mono text-bayside">{a.action}</span>
                                                    <span className="text-[10px] text-zinc-500 font-mono">{new Date(a.timestamp).toLocaleString("it-IT")}</span>
                                                </div>
                                                <div className="text-xs text-zinc-300 mt-1 font-grotesk">
                                                    {a.is_actor ? "Eseguita dal socio" : `Eseguita da ${a.actor_email || "—"}`}
                                                </div>
                                                {a.changes && a.changes.length > 0 && (
                                                    <p className="text-[11px] text-zinc-500 mt-1">Campi: {a.changes.join(", ")}</p>
                                                )}
                                                {a.metadata && (
                                                    <p className="text-[11px] text-zinc-500 mt-1">{JSON.stringify(a.metadata)}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Sticky save */}
                        {(tab === "anagrafica" || tab === "quote") && (
                            <div className="flex gap-3 mt-8 pt-6 border-t border-white/5">
                                <button onClick={save} disabled={saving} className="btn-rebel flex-1 justify-center" data-testid="detail-save">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}

function TabBtn({ active, onClick, children, testid, icon: Icon }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-grotesk uppercase tracking-widest transition whitespace-nowrap ${
                active ? "text-white border-b-2 border-bayside" : "text-zinc-500 hover:text-white"
            }`}
        >
            <Icon className="h-3.5 w-3.5" /> {children}
        </button>
    );
}

function SectionTitle({ children }) {
    return <div className="label-tag mt-2">— {children}</div>;
}

function MiniStat({ label, value, tone }) {
    const cl = tone === "bayside" ? "text-bayside" : tone === "magenta" ? "text-magenta" : tone === "emerald" ? "text-emerald-300" : tone === "vermillion" ? "text-vermillion" : "text-white";
    return (
        <div className="bento p-3">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-grotesk">{label}</div>
            <div className={`font-display text-xl mt-1 ${cl}`}>{value}</div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, tone }) {
    const cl = tone === "bayside" ? "text-bayside" : tone === "magenta" ? "text-magenta" : tone === "emerald" ? "text-emerald-300" : tone === "vermillion" ? "text-vermillion" : "text-zinc-300";
    return (
        <div className="bento p-3">
            <div className="flex items-center gap-2">
                {Icon && <Icon className={`h-3.5 w-3.5 ${cl}`} />}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">{label}</div>
            </div>
            <div className={`font-display text-2xl mt-2 ${cl}`}>{value}</div>
        </div>
    );
}

function DateRow({ icon: Icon, label, value }) {
    return (
        <div className="bento p-3 flex items-start gap-3">
            <Icon className="h-4 w-4 text-zinc-500 mt-0.5" />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">{label}</div>
                <div className="text-sm text-zinc-200 font-grotesk mt-1">
                    {value && value !== "—" ? new Date(value).toLocaleString("it-IT") : "—"}
                </div>
            </div>
        </div>
    );
}

function SocioCreate({ onClose, onCreated }) {
    const [form, setForm] = useState({ email: "", nome: "", cognome: "", quota_categories: [] });
    const [err, setErr] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setErr(null); setSaving(true);
        try {
            await apiPrivate.post("/admin/soci", form);
            onCreated();
        } catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };

    return (
        <Modal onClose={onClose} testid="socio-create-modal">
            <div className="p-8">
                <span className="label-tag">— Nuovo socio</span>
                <h2 className="font-display text-3xl tracking-tighter mt-2">Aggiungi al libro soci.</h2>
                <div className="grid grid-cols-2 gap-4 mt-6">
                    <Input label="Email *" v={form.email} on={(v) => setForm(s => ({ ...s, email: v }))} testid="c-email" />
                    <Input label="Nome" v={form.nome} on={(v) => setForm(s => ({ ...s, nome: v }))} testid="c-nome" />
                    <Input label="Cognome" v={form.cognome} on={(v) => setForm(s => ({ ...s, cognome: v }))} testid="c-cognome" />
                    <Input label="Codice Fiscale" v={form.codice_fiscale} on={(v) => setForm(s => ({ ...s, codice_fiscale: v }))} testid="c-cf" />
                </div>
                <div className="mt-4">
                    <div className="label-tag mb-2">Categorie quota</div>
                    <div className="flex gap-2">
                        {QUOTAS.map(q => (
                            <button key={q} onClick={() => setForm(s => ({ ...s, quota_categories: s.quota_categories.includes(q) ? s.quota_categories.filter(x => x !== q) : [...s.quota_categories, q] }))} data-testid={`c-q-${q}`} className={`px-4 py-2 text-xs uppercase tracking-widest font-grotesk border ${form.quota_categories.includes(q) ? "border-acid bg-acid text-black" : "border-zinc-800 text-zinc-500"}`}>{q}</button>
                        ))}
                    </div>
                </div>
                {err && <div className="mt-4 border border-magenta/40 bg-magenta/10 text-magenta px-3 py-2 text-sm" data-testid="create-err">{err}</div>}
                <div className="flex gap-3 mt-6">
                    <button onClick={submit} disabled={saving || !form.email} className="btn-rebel flex-1 justify-center" data-testid="create-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea socio"}</button>
                    <button onClick={onClose} className="btn-ghost">Annulla</button>
                </div>
            </div>
        </Modal>
    );
}

function SocioImport({ onClose, onDone }) {
    const [tipo, setTipo] = useState("persone-fisiche"); // 'persone-fisiche' | 'aziende'
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null); // {processed, total, status}

    const upload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        setProgress({ processed: 0, total: 0, status: "queued" });
        const fd = new FormData();
        fd.append("file", file);
        try {
            const res = await apiPrivate.post(`/admin/soci/import-excel/${tipo}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            const jobId = res.data.job_id;
            // Poll job status ogni 2s finché non completed/failed
            const pollInterval = setInterval(async () => {
                try {
                    const jr = await apiPrivate.get(`/admin/soci/import-jobs/${jobId}`);
                    const j = jr.data;
                    setProgress({ processed: j.progress?.processed || 0, total: j.progress?.total || 0, status: j.status });
                    if (j.status === "completed") {
                        clearInterval(pollInterval);
                        setUploading(false);
                        setResult({
                            created: j.result?.created || 0,
                            updated: j.result?.updated || 0,
                            errors: j.errors || [],
                            warnings: j.warnings || [],
                        });
                    } else if (j.status === "failed") {
                        clearInterval(pollInterval);
                        setUploading(false);
                        setResult({ error: j.result?.error || "Import fallito" });
                    }
                } catch (e) {
                    // ignora errori transient di polling
                }
            }, 2000);
        } catch (e) {
            setResult({ error: e?.response?.data?.detail || "Errore" });
            setUploading(false);
        }
    };

    const downloadTemplate = async (which) => {
        try {
            const res = await apiPrivate.get(`/admin/soci/import-template/${which}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `template_${which.replace("-", "_")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Errore download template");
        }
    };

    const tipoConfig = tipo === "persone-fisiche"
        ? {
            title: "Persone fisiche",
            headers: "Codice fiscale, Cognome, Nome, Email, PEC, Quota A/B/C/D, Data di nascita, Sesso, Nazione, Prov, Città, Indirizzo, CAP",
        }
        : {
            title: "Aziende",
            headers: "Denominazione / Attività, P.IVA, Socio abbinato, Tipo abbinamento, CF/P.IVA socio, PEC, Email, Email 2, Quota A/B/C/D",
        };

    return (
        <Modal onClose={onClose} testid="import-modal">
            <div className="p-8">
                <span className="label-tag">— Import bulk</span>
                <h2 className="font-display text-3xl tracking-tighter mt-2">Importa soci da Excel.</h2>

                {/* Tab selector tipo */}
                <div className="mt-6 grid grid-cols-2 gap-2">
                    <button
                        onClick={() => { setTipo("persone-fisiche"); setFile(null); setResult(null); }}
                        className={`p-3 rounded-xl border-2 text-left transition ${tipo === "persone-fisiche" ? "border-magenta bg-magenta/10" : "border-zinc-800 hover:border-zinc-700"}`}
                        data-testid="tab-pf"
                    >
                        <div className="font-display text-lg">Persone fisiche</div>
                        <div className="text-[10px] text-zinc-500 mt-1 font-grotesk">16 colonne · chiave: Email</div>
                    </button>
                    <button
                        onClick={() => { setTipo("aziende"); setFile(null); setResult(null); }}
                        className={`p-3 rounded-xl border-2 text-left transition ${tipo === "aziende" ? "border-magenta bg-magenta/10" : "border-zinc-800 hover:border-zinc-700"}`}
                        data-testid="tab-az"
                    >
                        <div className="font-display text-lg">Aziende</div>
                        <div className="text-[10px] text-zinc-500 mt-1 font-grotesk">12 colonne · chiave: Email</div>
                    </button>
                </div>

                <p className="text-zinc-400 mt-4 text-xs font-grotesk">Colonne previste ({tipoConfig.title}): {tipoConfig.headers}</p>

                {/* Download template */}
                <div className="mt-3 flex gap-2 flex-wrap">
                    <button onClick={() => downloadTemplate("persone-fisiche")} className="btn-ghost text-xs" data-testid="tpl-pf">
                        📥 Template Persone fisiche
                    </button>
                    <button onClick={() => downloadTemplate("aziende")} className="btn-ghost text-xs" data-testid="tpl-az">
                        📥 Template Aziende
                    </button>
                </div>

                <label htmlFor="file" className="block mt-6 border-2 border-dashed border-zinc-800 p-12 text-center cursor-pointer hover:border-acid transition" data-testid="dropzone">
                    <Upload className="h-8 w-8 mx-auto text-zinc-500" />
                    <div className="mt-4 font-grotesk">{file ? file.name : `Trascina o clicca per selezionare il file .xlsx ${tipoConfig.title}`}</div>
                </label>
                <input id="file" type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />

                {/* Progress bar durante l'import in background */}
                {progress && uploading && (
                    <div className="mt-4 border border-magenta/40 bg-magenta/5 p-4 font-grotesk text-sm" data-testid="import-progress">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-magenta uppercase tracking-widest text-xs">
                                {progress.status === "queued" ? "In coda…" : progress.status === "running" ? "Elaborazione in corso" : progress.status}
                            </span>
                            <span className="text-zinc-400 text-xs">
                                {progress.processed}/{progress.total || "?"} righe
                            </span>
                        </div>
                        <div className="w-full bg-zinc-900 rounded-full h-2 overflow-hidden">
                            <div
                                className="bg-magenta h-full transition-all duration-500"
                                style={{ width: progress.total > 0 ? `${Math.min(100, (progress.processed / progress.total) * 100)}%` : "5%" }}
                            />
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-2">Puoi chiudere questa finestra: l&apos;import continuerà in background.</p>
                    </div>
                )}

                {result && (
                    <div className="mt-4 border border-acid/40 bg-acid/5 p-4 font-grotesk text-sm" data-testid="import-result">
                        {result.error ? <span className="text-magenta">{result.error}</span> : (
                            <>
                                ✓ Importati: <strong>{result.created}</strong> nuovi · <strong>{result.updated}</strong> aggiornati
                                {result.errors?.length > 0 && (
                                    <div className="mt-2 text-magenta">
                                        <strong>{result.errors.length}</strong> errori:
                                        <ul className="mt-1 text-xs">
                                            {result.errors.slice(0, 5).map((e) => <li key={`err-${e.row}`}>Riga {e.row}: {e.error}</li>)}
                                        </ul>
                                    </div>
                                )}
                                {result.warnings?.length > 0 && (
                                    <div className="mt-2 text-acid">
                                        <strong>{result.warnings.length}</strong> avvertimenti (import comunque completato)
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="flex gap-3 mt-6">
                    <button onClick={upload} disabled={!file || uploading} className="btn-rebel flex-1 justify-center" data-testid="import-submit">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Carica ${tipoConfig.title}`}
                    </button>
                    <button onClick={() => { onDone(); }} className="btn-ghost" data-testid="import-close">Chiudi</button>
                </div>
            </div>
        </Modal>
    );
}

function Input({ label, v, on, type = "text", step, testid }) {
    return (
        <div>
            <div className="label-tag mb-2">{label}</div>
            <input
                type={type}
                step={step}
                value={v ?? ""}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-bayside rounded-xl"
            />
        </div>
    );
}


function HoldingsTab({ socioId, onChanged }) {
    const [holdings, setHoldings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await apiPrivate.get(`/admin/soci/${socioId}/holdings`);
            setHoldings(r.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [socioId]);

    const remove = async (h) => {
        if (!window.confirm(`Eliminare la quota ${h.categoria} (€${Number(h.valore_nominale).toLocaleString("it-IT")})?`)) return;
        try {
            await apiPrivate.delete(`/admin/soci/${socioId}/holdings/${h.id}`);
            toast.success("Quota eliminata");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore eliminazione");
        }
    };

    const totals = holdings.reduce((acc, h) => ({
        val: acc.val + (Number(h.valore_nominale) || 0),
        pct: acc.pct + (Number(h.percentuale_possesso) || 0),
    }), { val: 0, pct: 0 });

    return (
        <div className="mt-6 space-y-5" data-testid="tab-content-quote">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="text-lg font-display tracking-tighter">Portafoglio quote</h3>
                    <p className="text-xs text-zinc-500 mt-1">Dettaglio per ogni quota di investimento. Categoria + valore nominale.</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-rebel" data-testid="add-holding-btn">
                    <Plus className="h-4 w-4" /> Aggiungi quota
                </button>
            </div>

            <div className="bento p-4 grid grid-cols-2 gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Quote totali</div>
                    <div className="text-2xl font-display tracking-tighter mt-1">{holdings.length}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Valore nominale totale</div>
                    <div className="text-2xl font-display tracking-tighter mt-1 text-bayside">€{totals.val.toLocaleString("it-IT")}</div>
                </div>
            </div>

            {loading ? (
                <div className="p-8 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : holdings.length === 0 ? (
                <div className="bento p-8 text-center text-zinc-500" data-testid="holdings-empty">
                    <Briefcase className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    Nessuna quota di investimento registrata. Clicca "Aggiungi quota".
                </div>
            ) : (
                <div className="bento overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/40">
                            <tr>
                                <th className="text-left p-3 whitespace-nowrap">Categoria</th>
                                <th className="text-right p-3 whitespace-nowrap">Valore nominale</th>
                                <th className="text-left p-3 whitespace-nowrap">Acquisto</th>
                                <th className="text-left p-3">Note</th>
                                <th className="text-right p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {holdings.map(h => (
                                <tr key={h.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/20" data-testid={`holding-row-${h.id}`}>
                                    <td className="p-3">
                                        <span className="inline-block px-2.5 py-1 rounded-full bg-bayside/15 text-bayside font-semibold text-xs whitespace-nowrap">Quota {h.categoria}</span>
                                    </td>
                                    <td className="p-3 text-right font-mono whitespace-nowrap">€{Number(h.valore_nominale).toLocaleString("it-IT")}</td>
                                    <td className="p-3 text-xs text-zinc-400 whitespace-nowrap">{h.data_acquisto || "—"}</td>
                                    <td className="p-3 text-xs text-zinc-400 max-w-[220px] truncate" title={h.note || ""}>{h.note || "—"}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                        <div className="inline-flex gap-1.5">
                                            <button
                                                onClick={() => { setEditing(h); setShowForm(true); }}
                                                className="px-2.5 py-1 rounded-full border border-white/10 text-xs text-zinc-300 hover:border-bayside hover:text-bayside transition"
                                                data-testid={`edit-holding-${h.id}`}
                                                title="Modifica"
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                onClick={() => remove(h)}
                                                className="px-2.5 py-1 rounded-full border border-white/10 text-xs text-zinc-400 hover:border-vermillion hover:text-vermillion transition"
                                                data-testid={`delete-holding-${h.id}`}
                                                title="Elimina"
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && (
                <HoldingFormModal
                    socioId={socioId}
                    initial={editing}
                    onClose={() => setShowForm(false)}
                    onSaved={async () => { setShowForm(false); await load(); onChanged?.(); }}
                />
            )}
        </div>
    );
}

function HoldingFormModal({ socioId, initial, onClose, onSaved }) {
    const [form, setForm] = useState({
        categoria: initial?.categoria || "A",
        valore_nominale: initial?.valore_nominale ?? "",
        data_acquisto: initial?.data_acquisto || "",
        note: initial?.note || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        const val = parseFloat(form.valore_nominale);
        if (!form.categoria || isNaN(val)) {
            toast.error("Categoria e valore nominale sono obbligatori");
            return;
        }
        if (val < 0) {
            toast.error("Il valore nominale non può essere negativo");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                categoria: form.categoria,
                valore_nominale: val,
                // percentuale_possesso conservata nel DB come 0 di default (fonte legacy)
                percentuale_possesso: initial?.percentuale_possesso ?? 0,
                data_acquisto: form.data_acquisto || null,
                note: form.note || null,
            };
            if (initial) {
                await apiPrivate.patch(`/admin/soci/${socioId}/holdings/${initial.id}`, payload);
                toast.success("Quota aggiornata");
            } else {
                await apiPrivate.post(`/admin/soci/${socioId}/holdings`, payload);
                toast.success("Quota aggiunta");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="holding-form-modal">
            <div className="bg-[#0a0c1c] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-display tracking-tighter">{initial ? "Modifica" : "Aggiungi"} quota di investimento</h3>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Categoria *</label>
                        <div className="grid grid-cols-4 gap-2 mt-1">
                            {QUOTAS.map(q => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => setForm(s => ({ ...s, categoria: q }))}
                                    className={`py-2 rounded-xl border text-sm font-semibold transition ${form.categoria === q ? "border-bayside bg-bayside/15 text-bayside" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                                    data-testid={`hf-cat-${q}`}
                                >
                                    Quota {q}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Valore nominale (€) *</label>
                        <input type="number" min={0} step="0.01" value={form.valore_nominale} onChange={e => setForm(s => ({ ...s, valore_nominale: e.target.value }))} className="input-field" data-testid="hf-val" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Data acquisto</label>
                        <input type="date" value={form.data_acquisto} onChange={e => setForm(s => ({ ...s, data_acquisto: e.target.value }))} className="input-field" data-testid="hf-date" />
                    </div>
                    <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest">Note</label>
                        <textarea rows={2} value={form.note} onChange={e => setForm(s => ({ ...s, note: e.target.value }))} placeholder="Riferimento contratto, operazione, ecc." className="input-field" data-testid="hf-note" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="btn-ghost text-sm">Annulla</button>
                    <button onClick={submit} disabled={saving} className="btn-rebel" data-testid="hf-submit">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {initial ? "Salva modifiche" : "Aggiungi quota"}
                    </button>
                </div>
            </div>
        </div>
    );
}
